import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { formatCurrency } from '../../../utils/formatters';
import { S3Image, getAvatarStyle } from '../../../lib/s3';
import { openProfileDrawer } from './profileDrawerHelper';
import { SEND_NDA_TO_RECEIVING_PARTY } from '../../../graphql/mutations/dealMutations';
import { extractDealId } from '../../../utils/dealUtils';
import { useSuccessionTheme } from '../SuccessionThemeProvider';

const DealInterestListItem = ({
  dealInterest,
  dealName,
  companySettings: companySettingsProp,
  onSave,
  onInvite,
  onRemoveFromShortlist,
  onSendNda,
  onViewDetails,
  viewDetailsPath,
  viewDetailsLabel,
  scheduleMeetingPath,
  badgeText,
  badgeColor,
  allowShortlistMessaging = false
}) => {
  const intl = useIntl();
  const { company: themeCompanySettings } = useSuccessionTheme();
  const companySettings = companySettingsProp || themeCompanySettings;
  const resolvedViewDetailsLabel = viewDetailsLabel || intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.view_details' });
  const advisor = dealInterest?.inventory_user?.user;
  if (!advisor) return null;
  const avatarStyle = getAvatarStyle(advisor.profile_picture, companySettings);
  
  // Remove the internal SEND_NDA_TO_RECEIVING_PARTY mutation to avoid duplicates
  // The parent component (DealShortlisted) will handle the NDA sending
  
  // Function to handle sending NDA via parent callback
  const handleSendNda = () => {
    if (typeof onSendNda === 'function') {
      onSendNda(dealInterest.id);
    } else {
      console.error("No onSendNda callback provided");
      alert("Error: Unable to send NDA - no handler available");
    }
  };
  
  // Function to open messaging with this advisor
  const handleMessageClick = () => {
    if (advisor && advisor.user_name) {
      // Store the tab value (5 is the index for DealTab) in sessionStorage
      sessionStorage.setItem('profileDrawer_tab', '5');
      
      // Store the section to focus on
      sessionStorage.setItem('profileDrawer_focusOn', 'chat');
      
      // Open the profile drawer
      window.location.href = `/profiles/${advisor.user_name}`;
    }
  };
  
  // Function to open the user profile drawer
  const handleOpenProfile = () => {
    if (advisor && advisor.user_name) {
      openProfileDrawer(advisor.user_name, { fromDeal: true, dealInterest });
    }
  };
  
  // Determine advisor type (match, rolodex, follower)
  const getAdvisorType = () => {
    if (!dealInterest) return null;

    if (dealInterest.workflow_state === 'following') {
      return { type: 'rolodex', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.rolodex' }), classes: 'bg-succession-primary-10 text-succession-primary' };
    } else if (dealInterest.workflow_state === 'follower') {
      return { type: 'follower', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.follower' }), classes: 'bg-succession-accent-10 text-succession-accent' };
    } else if (dealInterest.reason_for_suggestion) {
      return { type: 'matches', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.suggested_match' }), classes: 'bg-succession-success-10 text-succession-success' };
    }

    return null;
  };

  // Determine workflow state (invited, shortlisted, etc.)
  const getWorkflowState = () => {
    if (!dealInterest) return null;

    if (dealInterest.workflow_state === 'invited') {
      return { type: 'invited', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.invited' }), classes: 'bg-succession-success-10 text-succession-success' };
    } else if (dealInterest.workflow_state === 'shortlisted') {
      return { type: 'shortlisted', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.shortlisted' }), classes: 'bg-succession-warning-10 text-succession-warning' };
    } else if (dealInterest.workflow_state === 'declined') {
      return { type: 'declined', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.declined' }), classes: 'bg-succession-error-bg text-succession-error' };
    } else if (dealInterest.workflow_state === 'reviewing_proposal') {
      return { type: 'reviewing', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.proposal_received' }), classes: 'bg-succession-primary-10 text-succession-primary' };
    } else if (dealInterest.workflow_state === 'out_for_receiving_signature') {
      return { type: 'nda_out', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.nda_out_for_review' }), classes: 'bg-succession-warning-10 text-succession-warning' };
    } else if (dealInterest.workflow_state === 'awaiting_disclosing_signature') {
      return { type: 'nda_awaiting', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.awaiting_your_signature' }), classes: 'bg-succession-accent-10 text-succession-accent' };
    } else if (dealInterest.workflow_state === 'nda_completed') {
      return { type: 'nda_completed', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.nda_completed' }), classes: 'bg-succession-success-10 text-succession-success' };
    } else if (dealInterest.workflow_state === 'negotiating') {
      return { type: 'negotiating', label: intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.negotiating' }), classes: 'bg-succession-secondary-10 text-succession-secondary' };
    }

    return null;
  };
  
  const advisorType = getAdvisorType();
  const workflowState = getWorkflowState();
  
  // Get badge color class based on color name
  const getBadgeColorClasses = (color) => {
    switch (color) {
      case 'blue':
        return 'bg-succession-primary-10 text-succession-primary';
      case 'green':
        return 'bg-succession-success-10 text-succession-success';
      case 'red':
        return 'bg-succession-error-bg text-succession-error';
      case 'yellow':
        return 'bg-succession-warning-10 text-succession-warning';
      case 'purple':
        return 'bg-succession-secondary-10 text-succession-secondary';
      case 'orange':
        return 'bg-succession-accent-10 text-succession-accent';
      case 'gray':
      default:
        return 'bg-succession-bg-tertiary text-succession-text-muted';
    }
  };
  
  // Render action buttons
  const renderActionButtons = () => {
    const actions = [];
    
    // Save button (available in matches, invited)
    if (onSave && !dealInterest.saved) {
      actions.push(
        <button
          key="save"
          onClick={() => onSave(dealInterest.id)}
          className="px-3 py-1 rounded text-xs font-medium bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg"
        >
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.save' })}
        </button>
      );
    }

    // Invite button (available in matches) - use workflow_state as source of truth
    if (onInvite && dealInterest.workflow_state === 'matching') {
      actions.push(
        <button
          key="invite"
          onClick={() => onInvite(dealInterest.id)}
          className="px-3 py-1 rounded text-xs font-medium bg-succession-primary text-white hover:bg-succession-primary-hover"
        >
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.invite' })}
        </button>
      );
    }

    // Remove from shortlist button
    if (onRemoveFromShortlist && dealInterest.workflow_state === 'shortlisted') {
      actions.push(
        <button
          key="remove"
          onClick={() => onRemoveFromShortlist(dealInterest.id)}
          className="px-3 py-1 rounded text-xs font-medium bg-succession-bg-secondary text-succession-text-secondary hover:bg-succession-hover-bg"
        >
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.remove' })}
        </button>
      );
    }

    // Shortlisted state: Message and Send NDA buttons
    if (dealInterest.workflow_state === 'shortlisted') {
      // Message button (if messaging is allowed for shortlisted)
      if (allowShortlistMessaging) {
        actions.push(
          <button
            key="message"
            onClick={handleMessageClick}
            className="px-3 py-1 rounded text-xs font-medium bg-succession-primary text-white hover:bg-succession-primary-hover flex items-center"
          >
            <span className="fa fa-envelope mr-1"></span>
            {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.message' })}
          </button>
        );
      }

      // Send NDA button - using GraphQL mutation directly
      actions.push(
        <button
          key="sendNda"
          onClick={handleSendNda}
          className="px-3 py-1 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 flex items-center"
        >
          <span className="fas fa-file-signature mr-1"></span>
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.send_nda' })}
        </button>
      );
      
      // If we're in shortlisted state and have added these actions, no need to check other conditions
      return (
        <div className="flex justify-end space-x-2">
          {actions}
        </div>
      );
    }
    
    // Schedule Meeting button/link
    if (scheduleMeetingPath) {
      let path = scheduleMeetingPath;
      if (typeof scheduleMeetingPath === 'function') {
        path = scheduleMeetingPath(dealInterest.id);
      }
      
      actions.push(
        <Link
          key="scheduleMeeting"
          to={path}
          className="px-3 py-1 rounded text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700"
        >
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.action.schedule' })}
        </Link>
      );
    }
    
    // View details button/link
    if (viewDetailsPath) {
      let path = viewDetailsPath;
      if (typeof viewDetailsPath === 'function') {
        path = viewDetailsPath(dealInterest.id);
      }
      
      actions.push(
        <Link
          key="viewDetails"
          to={path}
          className="px-3 py-1 rounded text-xs font-medium bg-succession-primary text-white hover:bg-succession-primary-hover"
        >
          {resolvedViewDetailsLabel}
        </Link>
      );
    } else if (onViewDetails) {
      actions.push(
        <button
          key="viewDetails"
          onClick={() => onViewDetails(dealInterest.id)}
          className="px-3 py-1 rounded text-xs font-medium bg-succession-primary text-white hover:bg-succession-primary-hover"
        >
          {resolvedViewDetailsLabel}
        </button>
      );
    }
    
    return (
      <div className="flex justify-end space-x-2">
        {actions}
      </div>
    );
  };
  
  // Status badges
  const renderStatusBadges = () => {
    const badges = [];
    
    // If custom badge is provided, use it as primary badge
    if (badgeText) {
      badges.push(
        <span 
          key="customBadge"
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getBadgeColorClasses(badgeColor || 'gray')}`}
        >
          {badgeText}
        </span>
      );
    }
    // Otherwise if workflow state badge is available, use it as primary badge
    else if (workflowState) {
      badges.push(
        <span 
          key="workflowState"
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${workflowState.classes}`}
        >
          {workflowState.label}
        </span>
      );
    }
    
    // Always add advisor type badge if it exists and hasn't been shown yet
    if (advisorType && 
        !(badgeText && badgeText.includes(advisorType.label)) && 
        !(workflowState && workflowState.label === advisorType.label)) {
      badges.push(
        <span 
          key="advisorType"
          className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${advisorType.classes}`}
        >
          {advisorType.label}
        </span>
      );
    }
    
    // Add saved badge if item is saved
    if (dealInterest.saved) {
      badges.push(
        <span
          key="saved"
          className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary-light text-succession-primary"
        >
          {intl.formatMessage({ id: 'succession_deals.deal_interest_card.badge.saved' })}
        </span>
      );
    }
    
    return badges.length > 0 ? (
      <div className="flex items-center mt-1">
        {badges}
      </div>
    ) : null;
  };
  
  return (
    <tr className="hover:bg-succession-hover-bg">
      <td className="px-6 py-4 max-w-xs">
        <div className="flex items-center min-w-0">
          <div
            className="h-10 w-10 flex-shrink-0 rounded-full flex items-center justify-center text-succession-text-secondary text-sm font-semibold overflow-hidden"
            style={{ backgroundColor: avatarStyle.backgroundColor }}
          >
            {advisor.profile_picture ? (
              <img 
                src={avatarStyle.url}
                alt={advisor.name}
                className="h-full w-full object-cover"
                style={avatarStyle.isNewStyle ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
              />
            ) : (
              advisor.name?.charAt(0).toUpperCase() || '?'
            )}
          </div>
          <div className="ml-3 min-w-0 flex-1">
            <div 
              className="text-sm font-medium text-succession-text-primary cursor-pointer hover:text-succession-primary truncate"
              onClick={handleOpenProfile}
              title={advisor.headline || advisor.name}
              style={{ maxWidth: '300px' }}
            >
              {advisor.headline || advisor.name}
            </div>
            {renderStatusBadges()}
          </div>
          <button 
            onClick={handleOpenProfile}
            className="ml-2 flex-shrink-0 p-1.5 text-succession-text-secondary hover:text-succession-primary rounded-full hover:bg-succession-hover-bg transition-colors duration-150"
            title="View profile"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-secondary">
        {advisor.city ? `${advisor.city}${advisor.province ? `, ${advisor.province}` : ''}` : '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-primary text-right">
        {formatCurrency(advisor.annual_revenue)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-succession-text-primary text-right">
        {formatCurrency(advisor.assets_under_management)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        {renderActionButtons()}
      </td>
    </tr>
  );
};

DealInterestListItem.propTypes = {
  dealInterest: PropTypes.object.isRequired,
  dealName: PropTypes.string.isRequired,
  companySettings: PropTypes.shape({
    avatar_background_color: PropTypes.string,
    avatar_directory: PropTypes.string,
    primary_color: PropTypes.string,
  }),
  onSave: PropTypes.func,
  onInvite: PropTypes.func,
  onRemoveFromShortlist: PropTypes.func,
  onSendNda: PropTypes.func,
  onViewDetails: PropTypes.func,
  viewDetailsPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  viewDetailsLabel: PropTypes.string,
  scheduleMeetingPath: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  badgeText: PropTypes.string,
  badgeColor: PropTypes.string,
  allowShortlistMessaging: PropTypes.bool
};

export default DealInterestListItem; 